import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, from, throwError, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { TwoFactorAuthService } from '../services/two-factor/two-factor-auth.service';
import Swal from 'sweetalert2';

@Injectable()
export class MfaInterceptor implements HttpInterceptor {
  private isRefreshingMfa = false;
  private mfaSubject: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);

  constructor(
    private authService: AuthService,
    private twoFactorService: TwoFactorAuthService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // 1. Inyectar llave de Idempotencia estática para operaciones críticas de escritura (POST/PATCH)
    let clonedReq = req;
    if (['POST', 'PATCH', 'PUT'].includes(req.method) && !req.headers.has('x-idempotency-key')) {
      const idempotencyKey = crypto?.randomUUID ? crypto.randomUUID() : (Math.random().toString(36).substring(2, 15) + Date.now().toString(36));
      clonedReq = req.clone({
        headers: req.headers.set('x-idempotency-key', idempotencyKey)
      });
    }

    return next.handle(clonedReq).pipe(
      catchError((error: HttpErrorResponse) => {
        // Capturar bloqueo MFA de NestJS
        if (error.status === 403 && error.error?.code === 'MFA_REQUIRED') {
          return this.handleMfaRequired(clonedReq, next, error);
        }
        return throwError(() => error);
      })
    );
  }

  private handleMfaRequired(req: HttpRequest<any>, next: HttpHandler, error: HttpErrorResponse): Observable<HttpEvent<any>> {
    if (!this.isRefreshingMfa) {
      this.isRefreshingMfa = true;
      this.mfaSubject.next(null);

      return from(this.promptForMfa()).pipe(
        switchMap(async (isValid) => {
          this.isRefreshingMfa = false;
          if (isValid) {
            this.mfaSubject.next(true);
            const newToken = this.authService.getMFAToken();
            const reClonedReq = req.clone({
              headers: req.headers.set('Authorization', `Bearer ${newToken}`)
            });
            return reClonedReq;
          } else {
            this.mfaSubject.next(false);
            throw error;
          }
        }),
        switchMap((reClonedReq) => next.handle(reClonedReq))
      );
    } else {
      // Si ya hay un modal abierto pidiendo el MFA, las demás peticiones que reboten se pausan 
      // y esperan a que el mfaSubject emita true (o false)
      return this.mfaSubject.pipe(
        filter(result => result !== null),
        take(1),
        switchMap(async (isValid) => {
          if (isValid) {
            const newToken = this.authService.getMFAToken();
            const reClonedReq = req.clone({
              headers: req.headers.set('Authorization', `Bearer ${newToken}`)
            });
            return next.handle(reClonedReq).toPromise() as Promise<HttpEvent<any>>;
          } else {
            throw error;
          }
        }),
        switchMap(obs => from(Promise.resolve(obs))) // Convertir la resolución asíncrona de nuevo a Observable
      );
    }
  }

  private async promptForMfa(): Promise<boolean> {
    const result = await Swal.fire({
      title: 'Validación de Seguridad',
      text: 'Tu sesión segura ha expirado. Ingresa tu código TOTP (Google Authenticator) para continuar con la operación:',
      input: 'text',
      inputAttributes: {
        autocapitalize: 'off',
        pattern: '[0-9]*',
        inputmode: 'numeric',
        maxlength: '6'
      },
      showCancelButton: true,
      confirmButtonText: 'Verificar y Continuar',
      cancelButtonText: 'Cancelar transacción',
      confirmButtonColor: '#0093A2',
      showLoaderOnConfirm: true,
      backdrop: true,
      allowOutsideClick: false,
      allowEscapeKey: false,
      preConfirm: async (code) => {
        if (!code || code.length < 6) {
          Swal.showValidationMessage('Ingresa los 6 dígitos completos');
          return false;
        }
        try {
          const isValid = await this.twoFactorService.verifyTokenAPI(code);
          if (!isValid) {
            throw new Error('Código incorrecto');
          }
          // storeMFAToken ya se ejecutó en verifyTokenAPI; aquí solo confirmamos
          return true;
        } catch (err) {
          Swal.showValidationMessage('Código incorrecto o expirado');
          return false;
        }
      }
    });

    return result.isConfirmed && result.value === true;
  }
}
